import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { WIDGET_TYPE_LABELS } from 'lib/element-config';
import { WixElement } from 'lib/gb-api';

interface WidgetCardProps {
  element: WixElement;
  onEdit: () => void;
  onDelete: () => void;
  onUseOnPage?: () => void;
}

export const WidgetCard = ({ element, onEdit, onDelete, onUseOnPage }: WidgetCardProps) => {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-gray-900">
            {element.name || element.display_id}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {WIDGET_TYPE_LABELS[element.type] ?? element.type}
            {element.campaign && ` · ${element.campaign.title}`}
          </p>
        </div>
        <Badge variant="pill">{element.display_id}</Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        {onUseOnPage && (
          <Button size="sm" variant="solid" color="primary" onClick={onUseOnPage}>
            Use on this page
          </Button>
        )}
        <Button size="sm" variant="outline" color="neutral" onClick={onEdit}>
          Edit
        </Button>
        <DeleteButton element={element} onDelete={onDelete} />
      </div>
    </div>
  );
};

const DeleteButton = ({ element, onDelete }: Pick<WidgetCardProps, 'element' | 'onDelete'>) => {
  return (
    <AlertDialog.Root type="error">
      <AlertDialog.Trigger asChild>
        <Button size="sm" variant="transparent" color="error">
          Delete
        </Button>
      </AlertDialog.Trigger>
      <AlertDialog.Portal>
        <AlertDialog.Overlay />
        <AlertDialog.Content>
          <AlertDialog.Header>
            <AlertDialog.Icon />
            <AlertDialog.Close />
          </AlertDialog.Header>
          <AlertDialog.Body>
            <AlertDialog.Title>Delete {element.name || element.display_id}?</AlertDialog.Title>
            <AlertDialog.Description>
              This action cannot be undone. Any page still using it will show a deleted widget until
              you remove it in the Wix editor.
            </AlertDialog.Description>
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action onClick={onDelete}>Delete widget</AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};
